/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { stringify } from 'querystring';

import {
	type InfiniteData,
	type QueryClient,
	type QueryKey,
	keepPreviousData,
	useInfiniteQuery,
} from '@tanstack/react-query';
import { isZoover } from '@vakantiesnl/components/src/utils';
import { getMinutesInMs } from '@vakantiesnl/services/src/util';
import { errorHandler, parseErrorToReport } from '@vakantiesnl/services/src/util/errorHandling';
import { sortObject } from '@vakantiesnl/services/src/util/sortObjectKeys';
import { fetchVakNL } from '@vakantiesnl/services/src/util/vaknl-fetch';
import { type Reviews } from '@vakantiesnl/types';

import { replaceUndefinedByNull } from '../../util/replaceUndefinedByNull';
import { mapReviewResponse } from '../utils/mapReviewsResponse';

export const DEFAULT_REVIEW_PAGE = 1;
export const DEFAULT_REVIEW_PAGE_SIZE = 6;

export const createReviewSearchRequest = ({ id, ...params }: Reviews.QueryParams): RequestInfo => {
	return new Request(`${process.env.NEXT_PUBLIC_REVIEW_ENDPOINT_URL}/api/reviews/${id}?${stringify(params)}`);
};

const DEFAULT_REVIEW_ASPECTS_LOCALE = 'nl';

export const createReviewAspectsRequest = (body: Reviews.EntityData): RequestInfo => {
	const item = JSON.stringify(sortObject({ ...body, locale: DEFAULT_REVIEW_ASPECTS_LOCALE }));

	return new Request(`${process.env.NEXT_PUBLIC_REVIEW_ENDPOINT_URL}/api/reviews/aspects`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: item,
	});
};

export const createProxyReviewAspectsRequest = (body: Reviews.EntityData): RequestInfo => {
	return new Request('/api/getAspects', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(body),
	});
};

function getReviewsQueryKey(params: Reviews.QueryParams) {
	return ['reviews', JSON.stringify(params)];
}

export function useGetReviews(params: Reviews.QueryParams) {
	const query = useInfiniteQuery<
		Reviews.MappedReviewResponse,
		Error,
		InfiniteData<Reviews.MappedReviewResponse, Reviews.MappedReviewResponse>,
		QueryKey,
		Reviews.PageParam
	>({
		queryKey: getReviewsQueryKey(params),
		queryFn: ({ pageParam: { page, ...pageParam } }) =>
			fetchReviews({ ...params, ...pageParam }, page).then(replaceUndefinedByNull),
		staleTime: getMinutesInMs(60),
		initialPageParam: {
			page: DEFAULT_REVIEW_PAGE,
			size: DEFAULT_REVIEW_PAGE_SIZE,
		},
		enabled: isZoover,
		getNextPageParam: (lastPage: Reviews.MappedReviewResponse) => ({
			size: DEFAULT_REVIEW_PAGE_SIZE,
			cursorId: lastPage.pageParam.cursorId,
			page: lastPage.pageParam.page,
		}),
		placeholderData: keepPreviousData,
	});

	return {
		reviewsData: query.data,
		fetchNextReviewPage: query.fetchNextPage,
		isLoading: query.isLoading,
	};
}

export async function prefetchReviews(
	queryClient: QueryClient,
	params: Reviews.QueryParams,
	page = DEFAULT_REVIEW_PAGE,
) {
	await queryClient.prefetchInfiniteQuery<
		Reviews.MappedReviewResponse,
		Error,
		InfiniteData<Reviews.MappedReviewResponse, Reviews.MappedReviewResponse>,
		QueryKey,
		Reviews.PageParam
	>({
		queryKey: getReviewsQueryKey(params),
		queryFn: ({ pageParam: { page, ...pageParam } }) =>
			fetchReviews({ ...params, ...pageParam }, page).then(replaceUndefinedByNull),
		initialPageParam: {
			page: page,
			size: DEFAULT_REVIEW_PAGE_SIZE,
		},
		getNextPageParam: (lastPage: Reviews.MappedReviewResponse) => ({
			size: DEFAULT_REVIEW_PAGE_SIZE,
			cursorId: lastPage.pageParam.cursorId,
			page: lastPage.pageParam.page,
		}),
	});
}

async function fetchReviews(params: Reviews.QueryParams, page = DEFAULT_REVIEW_PAGE) {
	params.size ??= DEFAULT_REVIEW_PAGE_SIZE;

	const query = createReviewSearchRequest(params);

	try {
		const response = await fetchVakNL({ input: query });
		if (!response.ok) {
			throw new Error(`Network response was not ok. HTTP Response status code: ${response.status}`);
		}
		const result: Reviews.ResponseBody = await response.json();

		return mapReviewResponse(result, {
			page,
			size: params.size,
			cursorId: result.metadata.cursorId,
		});
	} catch (e) {
		errorHandler.report(parseErrorToReport(e, 'Error fetching reviews'));

		throw new Error(`Cannot fetch reviews: ${e}`);
	}
}
